import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  CSpinner,
  CButton,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
  CTabs,
  CTabList,
  CTab,
  CTabContent,
  CTabPanel,
  CRow,
  CCol,
  CFormLabel,
  CFormInput,
  CFormSelect,
  CFormTextarea,
  CFormCheck,
  CModalFooter,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilPencil, cilPlus, cilTrash, cilSearch } from "@coreui/icons";

import styles from "./Customers.module.scss";

const AddCustomer = ({ visible, onClose, onCustomerAdded, customerData }) => {
  const [activeKey, setActiveKey] = useState("data");
  const [errors, setErrors] = useState({});
  const [positions, setPositions] = useState([]);
  const [filteredPositions, setFilteredPositions] = useState([]);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [showPositionDropdown, setShowPositionDropdown] = useState(false);
  const [customPosition, setCustomPosition] = useState("");
  const [selectedPositionId, setSelectedPositionId] = useState("");
  const positionSearchRef = useRef(null);
  const dropdownRef = useRef(null);
  const [contactPersons, setContactPersons] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  // Начальные данные
  const DEFAULT_CUSTOMER = {
    Name: "",
    FullName: "",
    Director: "",
    Address: "",
    Phone: "",
    ShortName: "",
    Email: "",
    Password: null,
    login: null,
    INN: "",
    RegNum: "",
    IsBidder: 0,
    IsMain: 0,
    IsCompleted: 0,
    DocPath: "",
    DeclFNR: "",
    DeclFND: "",
    AllowInfo: 0,
    NType: 0,
    idPost: "",
  };

  const [formData, setFormData] = useState(DEFAULT_CUSTOMER);

  // Функция загрузки должностей из API
  const fetchPositions = async (searchTerm = "") => {
    setLoadingPositions(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/typetable/?ntype=62", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && Array.isArray(response.data)) {
        // Сортируем по имени
        const sortedPositions = response.data
          .filter((item) => item.Name && item.Id)
          .sort((a, b) => a.Name.localeCompare(b.Name));

        setPositions(sortedPositions);

        // Фильтруем по поисковому запросу
        if (searchTerm) {
          const filtered = sortedPositions.filter((item) =>
            item.Name.toLowerCase().includes(searchTerm.toLowerCase()),
          );
          setFilteredPositions(filtered);
        } else {
          setFilteredPositions(sortedPositions);
        }
      }
    } catch (error) {
      console.error("Ошибка при загрузке должностей:", error);
      setPositions([]);
      setFilteredPositions([]);
    } finally {
      setLoadingPositions(false);
    }
  };

  // const fetchDataContacts = async () => {
  //   setLoadingContacts(true);
  //   try {
  //     const token = localStorage.getItem("token");
  //     const response = await axios.get("/api/admin/customer-contacts/", {
  //       headers: {
  //         Authorization: `Bearer ${token}`,
  //       },
  //     });

  //     setContactPersons(response.data);
  //     console.log(response.data);
  //   } catch (error) {
  //     console.error("Ошибка при получении списка контактных лиц", error);
  //     alert("Ошибка при загрузке данных. Проверьте подключение к серверу.");
  //   }
  // };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
    }));

    // Очищаем ошибку при изменении поля
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handlePositionSearch = (e) => {
    const value = e.target.value;
    setCustomPosition(value);

    // Показываем дропдаун при вводе
    if (value.length > 0) {
      setShowPositionDropdown(true);
      const filtered = positions.filter((item) =>
        item.Name.toLowerCase().includes(value.toLowerCase()),
      );
      setFilteredPositions(filtered);
    } else {
      setShowPositionDropdown(false);
      setFilteredPositions(positions);
    }

    // Сбрасываем выбранный ID если пользователь вводит текст
    setSelectedPositionId("");
    setFormData((prev) => ({ ...prev, idPost: "" }));
  };

  const handleSelectPosition = (positionId, positionName) => {
    setSelectedPositionId(positionId);
    setCustomPosition(positionName);
    setFormData((prev) => ({ ...prev, idPost: positionId }));
    setShowPositionDropdown(false);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.Name || formData.Name.trim() === "") {
      newErrors.Name = "Обязательное поле";
    }

    // Валидация email (если указан)
    if (
      formData.Email &&
      formData.Email.trim() !== "" &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email)
    ) {
      newErrors.Email = "Неверный формат email";
    }

    // Валидация ИНН (если указан)
    if (
      formData.INN &&
      formData.INN.trim() !== "" &&
      !/^\d{10}$|^\d{12}$/.test(formData.INN)
    ) {
      newErrors.INN = "ИНН должен содержать 10 или 12 цифр";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      alert("Пожалуйста, исправьте ошибки в форме");
      return;
    }

    const token = localStorage.getItem("token");
    const isEdit = !!customerData;
    const method = isEdit ? "put" : "post";
    const url = isEdit
      ? `/api/admin/customers/${customerData.Id}/`
      : `/api/admin/customers/`;

    // Подготавливаем данные
    const payload = {
      ...formData,
      IsBidder: Number(formData.IsBidder) || 0,
      IsMain: Number(formData.IsMain) || 0,
      IsCompleted: Number(formData.IsCompleted) || 0,
      AllowInfo: Number(formData.AllowInfo) || 0,
      NType: Number(formData.NType) || 0,
      idPost: formData.idPost ? Number(formData.idPost) : null,
    };

    // Очищаем пустые строки для необязательных полей
    const optionalFields = [
      "FullName",
      "Director",
      "Address",
      "Phone",
      "ShortName",
      "DeclFNR",
      "DeclFND",
      "Email",
      "Password",
      "login",
      "INN",
    ];

    optionalFields.forEach((field) => {
      if (
        payload[field] === "" ||
        payload[field] === null ||
        payload[field] === undefined
      ) {
        payload[field] = null;
      }
    });

    // Для RegNum - удаляем, чтобы бэкенд сам сгенерировал
    if (!isEdit) {
      delete payload.RegNum;
    }

    try {
      const response = await axios[method](url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 201 || response.status === 200) {
        alert(isEdit ? "Данные обновлены!" : "Заказчик добавлен!");
        onClose();
        if (onCustomerAdded) onCustomerAdded();
      }
    } catch (error) {
      console.log("ДЕТАЛИ ОШИБКИ:", error.response?.data);

      if (error.response?.data?.message) {
        alert("Ошибка: " + error.response.data.message);
      } else if (error.response?.data) {
        if (typeof error.response.data === "object") {
          const errorMessages = Object.values(error.response.data).flat();
          alert("Ошибки:\n" + errorMessages.join("\n"));
        } else {
          alert("Ошибка: " + JSON.stringify(error.response.data));
        }
      } else {
        alert("Произошла ошибка при сохранении");
      }
    }
  };

  // Загружаем список должностей при открытии модалки
  useEffect(() => {
    if (visible) {
      setErrors({});
      if (customerData) {
        setFormData(customerData);
        setSelectedPositionId(customerData.idPost || "");
      } else {
        setFormData(DEFAULT_CUSTOMER);
        setSelectedPositionId("");
      }
    }
  }, [customerData, visible]);

  // useEffect(() => {
  //   console.log("Текущий ключ:", activeKey);
  //   if (activeKey === "contact-persons" && visible) {
  //     fetchDataContacts();
  //   }
  // }, [activeKey]);

  // Обработчик клика вне дропдауна
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        positionSearchRef.current &&
        !positionSearchRef.current.contains(event.target)
      ) {
        setShowPositionDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // useEffect(() => {});

  return (
    <CModal
      className={styles.customers__addmodal}
      visible={visible}
      onClose={onClose}
      size="lg"
      backdrop="static"
    >
      <CModalHeader>
        <CModalTitle>
          {customerData ? "Редактировать заказчика" : "Добавить заказчика"}
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        <CTabs
          activeItemKey={activeKey}
          onActiveItemChange={(key) => {
            setActiveKey(key);
          }}
        >
          <CTabList variant="tabs">
            <CTab itemKey="data">Данные о заказчике</CTab>
            <CTab itemKey="contact-persons">Контактные лица</CTab>
            <CTab itemKey="admission-information">Информация по допуску</CTab>
          </CTabList>
          <CTabContent>
            <CTabPanel className="p-3" itemKey="data">
              <div style={{ fontSize: "12px", color: "#333" }}>
                <CRow className="mb-2 align-items-center">
                  <CCol sm={3} className="text-start pe-2">
                    <CFormLabel className="mb-2">
                      Наименование без <br /> организационной{"\u00A0"}формы:
                      <span className="text-danger">*</span>
                    </CFormLabel>
                  </CCol>
                  <CCol sm={9} className="d-flex align-items-center">
                    <CFormInput
                      size="sm"
                      name="Name"
                      value={formData.Name}
                      onChange={handleInputChange}
                      className={errors.Name ? "is-invalid" : ""}
                      placeholder="Введите наименование"
                    />
                    {errors.Name && (
                      <div className="invalid-feedback d-block">
                        {errors.Name}
                      </div>
                    )}
                  </CCol>
                </CRow>

                <CRow className="mb-2 align-items-center">
                  <CCol sm={3} className="text-start pe-2">
                    <CFormLabel className="mb-2">
                      Сокращенное c организационной формой:
                    </CFormLabel>
                  </CCol>
                  <CCol sm={9}>
                    <CFormInput
                      size="sm"
                      name="ShortName"
                      value={formData.ShortName}
                      onChange={handleInputChange}
                      placeholder="Сокращенное наименование"
                    />
                  </CCol>
                </CRow>

                <CRow className="mb-2 align-items-center">
                  <CCol sm={3} className="text-start pe-2">
                    <CFormLabel className="mb-2">
                      Полное наименование с организационной формой:
                    </CFormLabel>
                  </CCol>
                  <CCol sm={8}>
                    <CFormInput
                      size="sm"
                      name="FullName"
                      value={formData.FullName}
                      onChange={handleInputChange}
                      placeholder="Полное наименование"
                    />
                  </CCol>
                  <CCol sm={1} className="ps-0">
                    <CButton size="sm" color="light" className=" w-100">
                      <CIcon className="customers__svg" icon={cilPencil} />
                    </CButton>
                  </CCol>
                </CRow>

                <CRow className="mb-2 align-items-center">
                  <CCol sm={3} className="text-start pe-2">
                    <CFormLabel className="mb-2">ИНН:</CFormLabel>
                  </CCol>
                  <CCol sm={9}>
                    <CFormInput
                      size="sm"
                      name="INN"
                      value={formData.INN}
                      onChange={handleInputChange}
                      className={errors.INN ? "is-invalid" : ""}
                      placeholder="10 или 12 цифр"
                    />
                    {errors.INN && (
                      <div className="invalid-feedback d-block">
                        {errors.INN}
                      </div>
                    )}
                  </CCol>
                </CRow>

                <CRow className="mb-2 align-items-center">
                  <CCol sm={3} className="text-start pe-2">
                    <CFormLabel className="mb-2">Руководитель, ФИО:</CFormLabel>
                  </CCol>
                  <CCol sm={9}>
                    <CFormInput
                      size="sm"
                      name="Director"
                      value={formData.Director}
                      onChange={handleInputChange}
                      placeholder="ФИО руководителя"
                    />
                  </CCol>
                </CRow>

                {/* Поле должности с автодополнением */}
                <CRow className="mb-2 align-items-center">
                  <CCol sm={3} className="text-start pe-2">
                    <CFormLabel className="mb-2">Должность:</CFormLabel>
                  </CCol>
                  <CCol
                    sm={9}
                    style={{ position: "relative" }}
                    ref={positionSearchRef}
                  >
                    <div className="d-flex align-items-center">
                      <CFormInput
                        size="sm"
                        value={customPosition}
                        onChange={handlePositionSearch}
                        onFocus={() => {
                          if (customPosition.length > 0) {
                            setShowPositionDropdown(true);
                          }
                        }}
                        placeholder="Начните вводить должность..."
                      />
                      <CButton
                        size="sm"
                        color="light"
                        className="ms-1"
                        onClick={() => {
                          setShowPositionDropdown(!showPositionDropdown);
                          if (!showPositionDropdown) {
                            fetchPositions(customPosition);
                          }
                        }}
                      >
                        <CIcon className="customers__svg" icon={cilSearch} />
                      </CButton>
                    </div>

                    {showPositionDropdown && (
                      <div
                        ref={dropdownRef}
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          zIndex: 1000,
                          backgroundColor: "white",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          maxHeight: "200px",
                          overflowY: "auto",
                          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                        }}
                      >
                        {loadingPositions ? (
                          <div className="p-2 text-muted">Загрузка...</div>
                        ) : filteredPositions.length === 0 ? (
                          <div className="p-2 text-muted">Нет совпадений</div>
                        ) : (
                          filteredPositions.map((position) => (
                            <div
                              key={position.Id}
                              style={{
                                padding: "8px 12px",
                                cursor: "pointer",
                                borderBottom: "1px solid #f0f0f0",
                                backgroundColor:
                                  selectedPositionId === position.Id
                                    ? "#f0f0f0"
                                    : "white",
                              }}
                              onClick={() =>
                                handleSelectPosition(position.Id, position.Name)
                              }
                              onMouseEnter={(e) =>
                                (e.target.style.backgroundColor = "#f8f9fa")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.backgroundColor =
                                  selectedPositionId === position.Id
                                    ? "#f0f0f0"
                                    : "white")
                              }
                            >
                              {position.Name}
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    {/* Скрытое поле для отправки idPost */}
                    <input
                      type="hidden"
                      name="idPost"
                      value={selectedPositionId}
                      onChange={handleInputChange}
                    />

                    {loadingPositions && !showPositionDropdown && (
                      <small className="text-muted">
                        Загрузка списка должностей...
                      </small>
                    )}
                  </CCol>
                </CRow>

                <CRow className="mb-2">
                  <CCol sm={3} className="text-start pe-2">
                    <CFormLabel className="pt-1">Почтовый адрес:</CFormLabel>
                  </CCol>
                  <CCol sm={9}>
                    <CFormTextarea
                      size="sm"
                      name="Address"
                      value={formData.Address}
                      onChange={handleInputChange}
                      rows={3}
                      style={{ resize: "none" }}
                      placeholder="Почтовый адрес"
                    />
                  </CCol>
                </CRow>

                <CRow className="mb-2 align-items-center">
                  <CCol sm={3} className="text-start pe-2">
                    <CFormLabel>Телефон:</CFormLabel>
                  </CCol>
                  <CCol sm={9}>
                    <CFormInput
                      size="sm"
                      name="Phone"
                      value={formData.Phone}
                      onChange={handleInputChange}
                      placeholder="Номер телефона"
                    />
                  </CCol>
                </CRow>

                <CRow className="mb-2 align-items-center">
                  <CCol sm={3} className="text-start pe-2">
                    <CFormLabel>Адрес электронной почты:</CFormLabel>
                  </CCol>
                  <CCol sm={9}>
                    <CFormInput
                      size="sm"
                      type="email"
                      name="Email"
                      value={formData.Email}
                      onChange={handleInputChange}
                      className={errors.Email ? "is-invalid" : ""}
                      placeholder="email@example.com"
                    />
                    {errors.Email && (
                      <div className="invalid-feedback d-block">
                        {errors.Email}
                      </div>
                    )}
                  </CCol>
                </CRow>

                <CRow className="mb-2 align-items-center">
                  <CCol sm={3} className="text-start pe-2">
                    <CFormLabel className="mb-2">Логин:</CFormLabel>
                  </CCol>
                  <CCol sm={9}>
                    <CFormInput
                      size="sm"
                      name="login"
                      value={formData.login}
                      onChange={handleInputChange}
                      placeholder="Автоматически сгенерируется по ИНН"
                    />
                  </CCol>
                </CRow>

                <CRow className="mb-2 align-items-center">
                  <CCol sm={3} className="text-start pe-2">
                    <CFormLabel className="mb-2">Пароль:</CFormLabel>
                  </CCol>
                  <CCol sm={9}>
                    <CFormInput
                      size="sm"
                      type="password"
                      name="Password"
                      value={formData.Password}
                      onChange={handleInputChange}
                      placeholder="Будет сгенерирован автоматически"
                    />
                  </CCol>
                </CRow>

                <CRow className="mt-2 align-items-start">
                  <CCol sm={4} className="text-start pe-2">
                    <CFormLabel>Логотип:</CFormLabel>
                  </CCol>
                  <CCol sm={3}>
                    <div
                      className="border bg-white"
                      style={{ height: "60px" }}
                    ></div>
                  </CCol>
                  <CCol sm={2} className="d-flex flex-column gap-1">
                    <CButton size="sm" color="light" className="border py-0">
                      Добавить
                    </CButton>
                    <CButton size="sm" color="light" className="border py-0">
                      Удалить
                    </CButton>
                  </CCol>
                  <CCol sm={3} className="d-flex flex-column">
                    <CFormCheck
                      size="sm"
                      name="IsBidder"
                      label="Организатор торгов"
                      checked={formData.IsBidder === 1}
                      onChange={handleInputChange}
                    />
                    <CFormCheck
                      size="sm"
                      name="IsCompleted"
                      label="Завершен"
                      checked={formData.IsCompleted === 1}
                      onChange={handleInputChange}
                    />
                    <CFormCheck
                      size="sm"
                      name="IsMain"
                      label="Головная организация"
                      checked={formData.IsMain === 1}
                      onChange={handleInputChange}
                    />
                  </CCol>
                </CRow>
              </div>
            </CTabPanel>

            <CTabPanel itemKey="contact-persons">
              <div
                className="mb-3 d-flex customers__button-wrap customers__button-wrap--pg 
              "
              >
                <CButton className="button">
                  <CIcon
                    icon={cilPlus}
                    className="customers__svg customers__svg--plus"
                  />
                </CButton>
                <CButton className="button">
                  <CIcon icon={cilPencil} className="customers__svg" />
                </CButton>
                <CButton className="button">
                  <CIcon
                    icon={cilTrash}
                    className="customers__svg customers__svg--delete"
                  />
                </CButton>
              </div>
              {/* <div>
                {!loadingContacts ? (
                  <div className="text-center p-5">
                    <CSpinner color="primary" />
                    <h5 className="text-muted mt-2">Загрузка данных...</h5>
                  </div>
                ) : (
                  <CTable>
                    <CTableHead>
                      <CTableRow>
                        <CTableHeaderCell>ФИО</CTableHeaderCell>
                        <CTableHeaderCell>Должность</CTableHeaderCell>
                        <CTableHeaderCell>Контакнтый телефон</CTableHeaderCell>
                        <CTableHeaderCell>
                          Адрес электронной почты
                        </CTableHeaderCell>
                        <CTableHeaderCell>
                          Ответственный за согласование
                        </CTableHeaderCell>
                      </CTableRow>
                    </CTableHead>
                    <CTableBody>
                      {contactPersons.length > 0 ? (
                        contactPersons.map((item) => (
                          <CTableRow key={item.Id || item.id}>
                            <CTableDataCell>
                              {item.Name || item.name || "—"}
                            </CTableDataCell>
                            <CTableDataCell>
                              {item.Position || "—"}
                            </CTableDataCell>
                            <CTableDataCell>
                              {item.Phone || item.phone || "—"}
                            </CTableDataCell>
                            <CTableDataCell>
                              {item.Email || item.email || "—"}
                            </CTableDataCell>
                            <CTableDataCell>
                              {item.IsMain ? "✅" : "—"}
                            </CTableDataCell>
                          </CTableRow>
                        ))
                      ) : (
                        <CTableRow>
                          <CTableDataCell
                            colSpan="5"
                            className="text-center p-4"
                          >
                            Никого не нашли. Проверьте консоль (F12)
                          </CTableDataCell>
                        </CTableRow>
                      )}
                    </CTableBody>
                  </CTable>
                )}
              </div> */}
              {/* <div className="text-center p-5">
                  Список контактных лиц пуст 
                  </div> */}
            </CTabPanel>
          </CTabContent>
        </CTabs>

        <CModalFooter className="justify-content-between bg-light mt-3">
          <CButton color="light" className="border shadow-sm" size="sm">
            Загрузить по ИНН
          </CButton>
          <div className="d-flex gap-2">
            <CButton
              color="primary"
              size="sm"
              className="px-4"
              onClick={handleSave}
            >
              ОК
            </CButton>
            <CButton
              color="light"
              className="border px-4"
              size="sm"
              onClick={onClose}
            >
              Отмена
            </CButton>
          </div>
        </CModalFooter>
      </CModalBody>
    </CModal>
  );
};

export default AddCustomer;
